import { MouseEvent } from 'react';
import LLMIcon from 'components/LLMIcon/LLMIcon';
import { DeleteOutline } from '@mui/icons-material';
import { PromptModel } from 'api/model';
import ScopeIndicator from 'components/indicators/ScopeIndicator';
import useModel from 'api/prompts-models/useModel';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import { alpha, Box, Tooltip, Typography } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { EntityCard } from 'components/settingsHub/EntityCard';
import { FONT_MONO } from 'themes/tokens';

type Props = {
  model: PromptModel;
  onSelect: () => void;
};

const ModelItem = ({ model, onSelect }: Props) => {
  const { remove } = useModel({ modelId: model.id, model });
  const [showDeleteDialog, DeleteDialog] = useDialog(ConfirmationDialog);

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    showDeleteDialog(true);
  };

  return (
    <EntityCard onClick={onSelect} ariaLabel={`Edit model ${model.name}`} minHeight={224}>
      <ScopeIndicator isGlobal className="absolute left-3 top-3 z-10" />
      <div className="entity-card-actions absolute top-2 right-2 z-10 opacity-0 transition-opacity">
        <ActionIconButton size="small" color="error" title="Delete" onClick={handleDeleteClick}>
          <DeleteOutline fontSize="small" />
        </ActionIconButton>
      </div>

      <div className="flex h-full min-w-0 flex-col items-center justify-evenly gap-2.5 py-3 text-center">
        <LLMIcon provider={model.type} model={model.settings?.model} size={42} />
        <div className="min-w-0 max-w-full">
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.02rem' }} className="line-clamp-2 px-3">
            {model.name}
          </Typography>
          {model.settings?.model && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: FONT_MONO, fontSize: '0.78rem' }}
              className="mt-1 truncate px-3"
            >
              {model.settings.model}
            </Typography>
          )}
        </div>
        {model.is_local && (
          <Tooltip title="Local model - available in offline mode" placement="top" arrow>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.375,
                borderRadius: '999px',
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: 1,
                color: 'info.main',
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.16),
                border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
              }}
            >
              <CloudOffIcon sx={{ fontSize: 13 }} />
              Local
            </Box>
          </Tooltip>
        )}
      </div>

      <DeleteDialog
        title="Delete Model"
        onConfirm={() => remove.mutate()}
        buttonsSettings={{ confirmText: 'Delete', confirmColor: 'error' }}
      >
        <Typography>
          Are you sure you want to delete the model <strong>{model.name}</strong>? This action cannot be undone.
        </Typography>
      </DeleteDialog>
    </EntityCard>
  );
};

export default ModelItem;
